import {
  useState,
  memo,
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useCallback,
} from 'react';
import { useToggle } from 'react-use';
import _ from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQuery, gql } from '@apollo/client';
import { UpgradeItemsInput, UpgradeItems as UpgradeItemsTypes } from '~types';
import { useProfile } from '../profile/hooks';
import { useTranslation } from 'react-i18next';

import { Loader } from '../Loader';
import { SteamItemImage } from '../item';
import playAudio from '~frontend/utils/playAudio';
import Currency from '../Currency';
import { Price, divide, multiplyNumber } from '~ui/Price';
import {
  useItemRight,
  useUpgradeAmount,
  useMode,
  useStatusGame,
} from './hooks';

import IconProfile7 from '../../scss/images/icon-profile-7.svg';
import { useConfig } from '~components/hooks';

interface ItemsShopProps {
  filterSearch: string;
  filterOrder: boolean;
}

interface ItemsShopData {
  getUpgradeItems: UpgradeItemsTypes;
}

interface ItemsShopVars {
  input: UpgradeItemsInput;
}

const ItemsShop: FunctionComponent<ItemsShopProps> = memo(
  ({ filterOrder, filterSearch }) => {
    const [itemRight, setItemRight] = useItemRight();
    const [upgradeAmount, setUpgradeAmount] = useUpgradeAmount();
    const [mode] = useMode();
    const [, setStatusGame] = useStatusGame();
    const getProfile = useProfile();
    const getConfig = useConfig();

    const { t } = useTranslation();

    const {
      loading,
      error,
      data,
      fetchMore,
      variables,
      refetch,
      networkStatus,
    } = useQuery<ItemsShopData, ItemsShopVars>(
      gql`
        query ItemsShop($input: UpgradeItemsInput!) {
          getUpgradeItems(input: $input) {
            count
            getItems {
              id
              price
              getImage
              getName
              color
            }
          }
        }
      `,
      {
        notifyOnNetworkStatusChange: true,
        variables: {
          input: {
            limit: 15,
            offset: 0,
            filterSearch,
            filterOrder: {
              sortField: 'price',
              sortOrder: filterOrder ? 'desc' : 'asc',
            },
            maxPrice: multiplyNumber(upgradeAmount, 15),
            minPrice: upgradeAmount,
          },
        },
      },
    );

    const getUpgradeItems = data?.getUpgradeItems;
    const shopItems = getUpgradeItems?.getItems;

    useEffect(() => {
      refetch();
    }, [upgradeAmount, filterOrder]);

    const addItemToItemsRight = useCallback(
      (event: SyntheticEvent<HTMLElement>) => {
        event.preventDefault();

        const itemId = Number(event.currentTarget.dataset.id);

        const getItem = _.clone(_.find(shopItems, ['id', itemId]));

        if (!getItem) {
          return;
        }

        if (itemRight?.id === getItem.id) {
          playAudio(null, '14.mp3', 0.4);
          setItemRight(null);
          setStatusGame(0);
        } else {
          playAudio(null, '13.mp3', 0.4);
          setItemRight(getItem);

          if (mode === 1) {
            const sum = divide(getItem?.price, 15);

            const newSum = sum > getProfile?.money ? getProfile?.money : sum;

            setUpgradeAmount(upgradeAmount >= sum ? upgradeAmount : newSum);
          }
        }
      },
      [
        shopItems,
        itemRight?.id,
        setItemRight,
        setStatusGame,
        mode,
        getProfile?.money,
        setUpgradeAmount,
        upgradeAmount,
      ],
    );

    const onLoadMore = useCallback(async () => {
      try {
        await fetchMore({
          variables: _.merge({}, variables, {
            input: {
              offset: shopItems?.length,
            },
          }),
        });
      } catch {
        //
      }
    }, [fetchMore, variables, shopItems?.length]);

    if (loading && _.includes([1, 2], networkStatus)) {
      return <Loader />;
    } else if (error) {
      return <>ItemsShop | Error! {error.message}</>;
    }

    return (
      <div id="ItemsShop" className="scrool">
        <InfiniteScroll
          className="in-case-wrap"
          next={onLoadMore}
          dataLength={_.size(shopItems)}
          hasMore={_.size(shopItems) < getUpgradeItems?.count}
          loader={<Loader />}
          scrollableTarget="ItemsShop"
        >
          {_.map(shopItems, getItem => (
            <div
              className={`in-case-item ${
                itemRight?.id === getItem.id ? 'status-5' : 'status-4'
              }`}
              data-rar={getItem.color}
              key={getItem.id}
              onClick={addItemToItemsRight}
              data-id={getItem.id}
            >
              <div className="in-case-cost">
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={getItem.price} />
                  </>
                ) : (
                  <>
                    <Price sum={getItem.price} />
                    <Currency />
                  </>
                )}
              </div>

              {itemRight?.id === getItem.id && (
                <div className="in-case-status-default">
                  <div className="in-case-status-default-icon">
                    <img src={IconProfile7} alt="" />
                  </div>
                </div>
              )}

              <div className="in-case-item-wrap">
                <div className="img">
                  <div className="in-case-item-bg" />
                  {itemRight?.id === getItem.id && (
                    <div className="img-contracts">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                      >
                        <g transform="translate(32 32) rotate(180)">
                          <g
                            fill="#8847ff"
                            stroke="#707070"
                            strokeWidth="1"
                            opacity="0"
                          >
                            <rect width="32" height="32" stroke="none"></rect>
                            <rect
                              x="0.5"
                              y="0.5"
                              width="31"
                              height="31"
                              fill="none"
                            ></rect>
                          </g>
                          <path
                            d="M13,19H7V11H0L10,0,20,11H13Z"
                            transform="translate(26 26) rotate(180)"
                            fill="#8847ff"
                            stroke="rgba(0,0,0,0)"
                            strokeMiterlimit="10"
                            strokeWidth="1"
                          ></path>
                        </g>
                      </svg>
                      <span>{t('In upgrade')}</span>
                    </div>
                  )}
                  <SteamItemImage url={getItem.getImage} size="350x350" />
                </div>
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    {_.truncate(getItem.getName, {
                      length: 35,
                      separator: '...',
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </InfiniteScroll>
      </div>
    );
  },
);

function ItemsShopView() {
  const { t } = useTranslation();
  const [filterOrder, setFilterOrder] = useToggle(true);
  const [filterSearch, setFilterSearch] = useState('');

  const onSetFilterOrder = (event: SyntheticEvent) => {
    event.preventDefault();

    setFilterOrder();
  };

  const onSetFilterSearch = (
    event: SyntheticEvent<HTMLInputElement | HTMLAnchorElement>,
  ) => {
    event.preventDefault();

    const target = event.target as Element & HTMLInputElement;

    const el = target.parentElement;

    setFilterSearch(
      event.type === 'click' ? el.querySelector('input').value : target.value,
    );
  };

  return (
    <div className="upgrade-items-shop">
      <div className="upgrade-items-title">
        <div className="title">{t('For an upgrade')}</div>
        <div className="search-block">
          <input
            type="text"
            placeholder={t('Quick search')}
            value={filterSearch}
            onChange={onSetFilterSearch}
          />
          <a className="btn-search" href="#" onClick={onSetFilterSearch}>
            <span className="material-icons">search</span>
          </a>
        </div>
        <a
          className={`in-case-sort ${filterOrder && 'active'}`}
          href="#"
          onClick={onSetFilterOrder}
        >
          <span className="icon material-icons">attach_money</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="42"
            height="42"
            viewBox="0 0 42 42"
          >
            <path
              d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
              transform="translate(-1981.333 -1356.333)"
              fill="#3B1674"
            ></path>
          </svg>
        </a>
      </div>
      <div className="upgrade-items-content">
        <ItemsShop filterOrder={filterOrder} filterSearch={filterSearch} />
      </div>
    </div>
  );
}

export default ItemsShopView;
